import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { AlertTriangle, Cpu, Globe, Clock, DollarSign, X } from "lucide-react";
import { endpoints } from "@/lib/api";
import { Spinner } from "@/components/ui";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-white/5 last:border-0" data-testid={testid}>
      <span className="flex items-center gap-2 text-[12.5px] text-white/60"><Icon size={13} className="text-gold" /> {label}</span>
      <span className="font-mono text-[13px] text-white">{value}</span>
    </div>
  );
}

export default function CostConfirmModal({ title, items, llm_calls, web_calls, estimated_seconds, op, units, onResolve }) {
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(!!op);

  useEffect(() => {
    if (!op) return;
    endpoints.usageEstimate(op, units || 1)
      .then((r) => setEst(r.data))
      .catch(() => setEst(null))
      .finally(() => setLoading(false));
  }, [op, units]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onResolve(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onResolve]);

  const llm = est ? est.llm_calls : llm_calls;
  const web = est ? est.web_calls : web_calls;
  const secs = est ? est.estimated_seconds : estimated_seconds;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md" onClick={() => onResolve(false)} data-testid="cost-modal-overlay">
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 0.2 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg overflow-hidden shadow-2xl"
        style={{ background: "#0B1530", border: "1px solid rgba(197,160,89,0.35)" }}
        data-testid="cost-modal"
      >
        <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#C5A059,#D4AF37 40%,transparent)" }} />
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <div className="overline text-[9px] text-gold mb-1.5">Cost confirmation</div>
              <h3 className="font-display text-xl leading-snug text-white" data-testid="cost-modal-title">{title}</h3>
            </div>
            <button onClick={() => onResolve(false)} className="text-white/40 hover:text-white" data-testid="cost-modal-close"><X size={18} /></button>
          </div>

          {items?.length > 0 && (
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mb-1.5">Scope</div>
              <ul className="space-y-1">
                {items.map((it, i) => (
                  <li key={i} className="text-[12.5px] text-white/75 flex gap-2"><span className="text-gold">•</span>{it}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-md px-4 py-1 mb-3" style={{ background: "rgba(21,42,85,0.55)", border: "1px solid rgba(255,255,255,0.06)" }}>
            {loading ? (
              <div className="py-4 flex items-center gap-2 text-[12px] text-white/50"><Spinner size={12} /> Loading real usage data…</div>
            ) : (
              <>
                <Stat icon={Cpu} label="LLM calls (Claude + GPT-5.2)" value={`~${llm}`} testid="cost-modal-llm" />
                <Stat icon={Globe} label="Tavily web lookups" value={`~${web}`} testid="cost-modal-web" />
                {est && <Stat icon={DollarSign} label="Estimated cost" value={`$${est.cost_usd.toFixed(2)}`} testid="cost-modal-usd" />}
                {secs != null && <Stat icon={Clock} label="Expected runtime" value={`~${secs}s`} testid="cost-modal-time" />}
              </>
            )}
          </div>
          <div className="text-[10.5px] text-white/40 mb-5" data-testid="cost-modal-basis">
            {est ? `Basis: ${est.basis}.` : "Basis: static estimate."} Calls are billed to the Emergent Universal LLM Key and your Tavily plan.
          </div>

          <div className="flex items-center gap-2 text-[11px] text-amber-300/80 mb-5">
            <AlertTriangle size={12} /> This action hits paid providers.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => onResolve(false)} className="px-4 py-2 text-[13px] rounded-md text-white/70 hover:text-white border border-white/10 hover:border-white/30 transition-colors" data-testid="cost-modal-cancel">Cancel</button>
            <button onClick={() => onResolve(true)} disabled={loading} className="px-5 py-2 text-[13px] rounded-md font-medium bg-[#C5A059] text-black hover:bg-[#D4AF37] transition-colors disabled:opacity-50" data-testid="cost-modal-confirm">Proceed</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
